import { installDraftingBehavior } from "./drafting-behavior";
import { installMouseButtonRecovery } from "./mouse-button-recovery";
import { getPreferences } from "./preferences";
import { setZoomPercent } from "./ZoomControl";
import { installCanvasNavigation } from "./canvas-navigation";
import { isWebLink } from "../shared/links";
import { nextSheetPosition, sheetAt } from "./sheet-operations";
import { installSheetSelection } from "./sheet-selection";
import { pasteAtCanvasPoint } from "./canvas-clipboard";
import { installSourceBehavior } from "./source-behavior";
import { useState, useRef, useMemo } from "react";

import type { CanvasEditor } from "./canvas/engine/editor";
import type { Sheet } from "../shared/schema";
import { AssetRecordType, DefaultColorStyle, DefaultFontStyle, type TLUser } from "@tldraw/tlschema";
import { atom } from "@tldraw/state";
import type { CanvasContent, Editor, VecLike } from "./canvas";
import { useBoardSync } from "./canvas/sync-client";
import { Store } from "@tldraw/store";
import type { TLStore } from "@tldraw/tlschema";
import { BoardCanvas } from "./BoardCanvas";

import { schema } from "../shared/schema";
import { notify, type User, type Room } from "./api";
import { createImageAssetStore } from "./image-assets";
import { ImageImports } from "./image-imports";
import { createImageOutbox } from "./image-outbox";
import { ImageUploads } from "./ImageUploads";
import { updateLegacyCreativeLabels } from "./creative-labels";
import { seedDemo } from "./documents";

import { WorkspaceUI } from "./Workspace";
import { DocumentAccess } from "./DocumentAccess";
import { canEdit as roleCanEdit } from "../shared/authorization";
export function Board({
  room,
  user,
  back,
  signOut,
  saveName,
  libraryItem,
}: {
  libraryItem?: string;
  room: Room;
  user: User;
  back: () => void;
  signOut?: () => Promise<void>;
  saveName?: (name: string) => Promise<void>;
}) {
  const [editor, setEditor] = useState<Editor | null>(null),
    [busy, setBusy] = useState(""),
    seeded = useRef(false);
  const [documentSessionId, setDocumentSessionId] = useState<string | null>(null);
  const [imports, setImports] = useState<ImageImports | null>(null);
  const assets = useMemo(
    () => createImageAssetStore(room.id, room.platform === "cloudflare"),
    [room.id, room.platform],
  );
  const presenceUser = useMemo(
    () =>
      atom<TLUser | null>("presence user", {
        id: `user:${user.id}` as TLUser["id"],
        typeName: "user",
        name: user.name,
        color: "#d65331",
        imageUrl: "",
        meta: {},
      }),
    [user.id, user.name],
  );
  const store = useBoardSync({
    uri: `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/rooms/${room.id}/sync`,
    schema,
    user: presenceUser,
    createStore: (collaboration) =>
      new Store({
        schema,
        props: {
          defaultName: "",
          assets,
          users: { currentUser: presenceUser, resolve: () => presenceUser },
          onMount: () => {},
          collaboration,
        } as never,
      }) as unknown as TLStore,
    onCustomMessage: (value: unknown) => {
      if (value && typeof value === "object" && "type" in value &&
        value.type === "document-session" && "sessionId" in value &&
        typeof value.sessionId === "string") {
        setDocumentSessionId(value.sessionId);
        return;
      }
      if (
        value &&
        typeof value === "object" &&
        "type" in value &&
        value.type === "board-error" &&
        "message" in value &&
        typeof value.message === "string"
      )
        notify(value.message);
    },
  });
  const connected =
    store.status === "synced-remote" && store.connectionStatus === "online";
  const documentAccess = useMemo(() => ({ roomId: room.id, userId: user.id,
    connected, canEdit: roleCanEdit(room.role), sessionId: documentSessionId }),
    [room.id, room.role, user.id, connected, documentSessionId]);
  // Board setup once the canvas is mounted.
  const mountBoard = (ed: Editor) => {
      // Development builds expose the editor for scripted checks.
      if (import.meta.env.DEV) Object.assign(window, { __planroomEditor: ed });
      if (room.role === "owner" || room.role === "editor")
        updateLegacyCreativeLabels(ed);
      const disposeNavigation = installCanvasNavigation(ed);
      const disposeMouseRecovery = installMouseButtonRecovery(
        ed.getContainer(),
      );
      const disposeSources = installSourceBehavior(ed);
      const disposeDrafting = installDraftingBehavior(ed);
      const disposeSelection = installSheetSelection(ed);
      // Pasted links that cannot be embedded become link cards.
      const linkAsset = async ({ url }: { url: string }) => {
        if (!isWebLink(url))
          throw new Error(
            "Use an http or https link without embedded credentials.",
          );
        const parsed = new URL(url);
        return AssetRecordType.create({
          type: "bookmark",
          props: {
            src: url,
            title: parsed.hostname,
            description:
              parsed.pathname === "/"
                ? "Open this website"
                : parsed.pathname,
            image: "",
            favicon: "",
          },
        });
      };
      (ed as unknown as CanvasEditor).registerExternalAssetHandler("url", linkAsset);
      // A single pasted sheet goes to the next free sheet position.
      (ed as unknown as CanvasEditor).registerExternalContentHandler(
        "content",
        ({ content, point }: { content: CanvasContent; point?: VecLike }) => {
          const roots = content.shapes.filter((s) => content.rootShapeIds.includes(s.id));
          const sheet = roots.length === 1 && roots[0].type === "sheet" ? (roots[0] as Sheet) : undefined;
          const next = ed as unknown as CanvasEditor;
          if (!sheet) {
            next.putContentOntoCurrentPage(content, { point });
            return;
          }
          const to = nextSheetPosition(ed, sheet.props.w, sheet.props.h, sheet.x, sheet.y);
          next.putContentOntoCurrentPage(content, { point: to });
          ed.zoomToBounds(ed.getSelectionPageBounds()!, { inset: 180, animation: { duration: 200 } });
        },
      );
      // The canvas menu's Paste reads the system clipboard.
      pasteAtCanvasPoint.set(ed, (point) => (ed as unknown as CanvasEditor).pasteFromClipboard(point));
      setEditor(ed);
      const images = new ImageImports(
        ed,
        undefined,
        createImageOutbox(user.id, room.id),
        assets.previews,
        {
          userId: user.id,
          maxBytes:
            room.platform === "cloudflare" ? 20_000_000 : undefined,
        },
      );
      setImports(images);
      void images.restore();
      const beforeUnload = (event: BeforeUnloadEvent) => {
        if (images.hasUnsavedFiles()) {
          event.preventDefault();
          event.returnValue = "";
        }
      };
      window.addEventListener("beforeunload", beforeUnload);
      const beforeNavigation = (event: Event) => {
        if (!images.hasUnsavedFiles()) return;
        event.preventDefault();
        notify(
          "Some images are not saved on this device yet. Wait for them to save, or retry or forget the failed uploads before leaving.",
        );
      };
      window.addEventListener(
        "planroom:before-navigation",
        beforeNavigation,
      );
      const container = ed.getContainer();
      const clearDropHint = () => ed.setHintingShapes([]);
      const dropHint = (event: DragEvent) => {
        if (!event.dataTransfer?.types.includes("Files")) return;
        const target = sheetAt(
          ed,
          ed.screenToPage({ x: event.clientX, y: event.clientY }),
        );
        ed.setHintingShapes(target ? [target.id] : []);
      };
      container.addEventListener("dragover", dropHint);
      container.addEventListener("dragleave", clearDropHint);
      container.addEventListener("drop", clearDropHint);
      ed.user.updateUserPreferences({ isSnapMode: true });
      ed.setStyleForNextShapes(DefaultFontStyle, "sans");
      ed.setStyleForNextShapes(DefaultColorStyle, "black");
      ed.updateInstanceState({ isGridMode: true });
      if (
        room.demo &&
        room.role === "owner" &&
        !ed.getCurrentPageShapes().length &&
        !seeded.current
      ) {
        seeded.current = true;
        setBusy("Preparing the example drawing…");
        seedDemo(ed, room.id)
          .catch((e) => notify(e.message))
          .finally(() => setBusy(""));
      } else if (ed.getCurrentPageShapes().length)
        setTimeout(() => {
          const bounds = ed.getCurrentPageBounds();
          if (getPreferences(user.id).openingView === "100")
            setZoomPercent(ed, 100);
          else if (bounds) ed.zoomToBounds(bounds, { inset: 220 });
        }, 150);
      return () => {
        if (import.meta.env.DEV) Object.assign(window, { __planroomEditor: undefined });
        window.removeEventListener("beforeunload", beforeUnload);
        window.removeEventListener(
          "planroom:before-navigation",
          beforeNavigation,
        );
        container.removeEventListener("dragover", dropHint);
        container.removeEventListener("dragleave", clearDropHint);
        container.removeEventListener("drop", clearDropHint);
        images.dispose();
        disposeMouseRecovery();
        disposeNavigation();
        disposeSources();
        disposeDrafting();
        disposeSelection();
      };
  };
  return (
    <DocumentAccess.Provider value={documentAccess}>
    <div className="board">
      <div className="canvas-area">
        {store.status === "synced-remote" && (
          <BoardCanvas store={store.store} onMount={mountBoard} />
        )}
      </div>
      {/* Terminal sync errors unmount the canvas; its controls must leave too. */}
      {editor && store.status === "synced-remote" && (
        <WorkspaceUI
          libraryItem={libraryItem}
          editor={editor}
          documentSessionId={documentSessionId}
          room={room}
          user={user}
          connected={connected}
          saveName={saveName}
          busy={busy}
          setBusy={setBusy}
          back={() => {
            if (imports?.hasUnsavedFiles()) {
              notify(
                "Some images are not saved on this device yet. Wait for them to save, or retry or forget the failed uploads before leaving.",
              );
              return;
            }
            back();
          }}
          signOut={
            signOut
              ? async () => {
                  if (imports?.hasUnsavedFiles()) {
                    notify(
                      "Some images are not saved on this device yet. Wait for them to save, or retry or forget the failed uploads before signing out.",
                    );
                    return;
                  }
                  await signOut();
                }
              : undefined
          }
        />
      )}
      {imports && <ImageUploads imports={imports} />}
      {(!editor || store.status !== "synced-remote") && (
        <div className="loading">
          {store.status === "error" ? (
            <>
              <h2>Cannot open this board</h2>
              <p>{store.error.message}</p>
              <button onClick={back}>Back to projects</button>
            </>
          ) : (
            <p>Connecting to the shared board…</p>
          )}
        </div>
      )}
    </div>
    </DocumentAccess.Provider>
  );
}
